import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useRouter } from "next/router"

import {
  useTestSetCloneAction,
  useTestSetDeleteAction,
} from "@/test-set/actions"
import {
  isDuplicateTestSetNameError,
  isPersistedTestSetId,
  mapUpdateTestSetInputToTransport,
  useInvalidateTestSetTransportQueries,
  useTestSetMetadataTransport,
  useTestSetRecordsTransport,
  useTestSetsListTransport,
  useUpdateTestSetTransport,
} from "@/test-set/transport"
import { EditableTitleSaveError } from "@coval/ui"

import { TRACKING_EVENTS, useAnalytics } from "@/lib/analytics"
import { useQueryParams } from "@/hooks/useQueryParams"
import { downloadBlob } from "@/utils/download"
import {
  isSameResourceName,
  resourceNameIssue,
} from "@/utils/validation/resourceName"

import { EVENTS, useEventDispatcher } from "../event-bus"
import { useLiveTestSetDisplayName } from "../hooks/useLiveTestSetEditorState"
import { buildTestSetCsvContent } from "./testSetCsvExport"

// Mirrors the backend TestSet.display_name max_length.
export const TEST_SET_NAME_MAX_LENGTH = 100

const duplicateTestSetNameMessage = (name: string) =>
  `A test set named ${name} already exists.`

export const useTestSetBreadcrumb = (providedTestSetId?: string) => {
  const router = useRouter()
  const queryParams = useQueryParams()
  const invalidateTestSetTransportQueries =
    useInvalidateTestSetTransportQueries()
  const dispatch = useEventDispatcher()
  const { trackEvent } = useAnalytics()

  const isTestSetDetailPage =
    router.pathname === "/[slug]/test-sets/[id]" ||
    router.pathname === "/[slug]/test-sets/[id]/index"
  const testSetId =
    providedTestSetId ||
    (isTestSetDetailPage ? (router.query.id as string | undefined) : undefined)

  const { data: testSetMetadata, isLoading: metadataIsLoading } =
    useTestSetMetadataTransport(testSetId || "", { enabled: !!testSetId })

  const {
    data: records,
    isLoading: recordsIsLoading,
    isError: recordsIsError,
    error: recordsError,
  } = useTestSetRecordsTransport(
    { test_set_id: testSetId || "" },
    { enabled: !!testSetId },
  )

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)

  const updateTestSetMutation = useUpdateTestSetTransport()

  const isPersisted = isPersistedTestSetId(testSetId)

  const liveDisplayName = useLiveTestSetDisplayName(
    isPersisted ? (testSetId ?? "") : "",
  )
  // The editor publishes "" until it hydrates, so an empty live name falls back to the saved one.
  const currentDisplayName =
    liveDisplayName || testSetMetadata?.metadata?.display_name

  const { data: testSets } = useTestSetsListTransport({
    enabled: Boolean(testSetId),
  })
  const otherTestSetNames = useMemo(
    () =>
      (testSets ?? [])
        .filter((testSet) => testSet.id !== testSetId)
        .map((testSet) => testSet.display_name),
    [testSets, testSetId],
  )
  const knownTestCaseCount =
    records?.length ?? testSetMetadata?.metadata?.test_case_count

  const { cloneTestSet, isCloning } = useTestSetCloneAction({
    // The editor leaves for the list once the copy exists.
    onCloned: useCallback(() => {
      const slug = router.query.slug as string
      void router.push(queryParams.getHref(`/test-sets`, slug))
    }, [queryParams, router]),
  })

  const handleCloneTestSet = useCallback(
    (): Promise<void> =>
      cloneTestSet({ testSetId, testCaseCount: knownTestCaseCount }),
    [cloneTestSet, knownTestCaseCount, testSetId],
  )

  const handleDeleteTestSet = useCallback(() => {
    if (!testSetId) {
      return
    }
    setIsDeleteModalOpen(true)
  }, [testSetId])

  const { deleteTestSet, isDeleting } = useTestSetDeleteAction({
    onDeleted: useCallback(() => {
      setIsDeleteModalOpen(false)
      const slug = router.query.slug as string
      void router.push(queryParams.getHref(`/test-sets`, slug))
    }, [queryParams, router]),
  })

  const handleConfirmDelete = useCallback(
    (): Promise<void> => deleteTestSet(testSetId),
    [deleteTestSet, testSetId],
  )

  const handleCancelDelete = useCallback(() => {
    setIsDeleteModalOpen(false)
  }, [])

  const handleDownload = useCallback(() => {
    if (!records || !testSetMetadata?.metadata?.display_name) {
      return
    }

    trackEvent(TRACKING_EVENTS.TEST_SET.CSV_DOWNLOADED, {
      test_set_id: testSetId,
      test_case_count: records.length,
    })

    const csvContent = buildTestSetCsvContent(records)
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" })

    downloadBlob(blob, `${testSetMetadata.metadata.display_name}.csv`)
  }, [records, testSetMetadata?.metadata?.display_name, trackEvent, testSetId])

  const validateTestSetName = useCallback(
    (name: string) =>
      resourceNameIssue(name, {
        resource: "test set",
        maxLength: TEST_SET_NAME_MAX_LENGTH,
        // Matches the test-set row rename: names that differ only in case or surrounding spaces count as taken.
        validate: (trimmed) =>
          otherTestSetNames.some((other) => isSameResourceName(other, trimmed))
            ? duplicateTestSetNameMessage(trimmed)
            : undefined,
      }),
    [otherTestSetNames],
  )

  // The editor event bus is process-wide, so a rename that settles after the page has moved to another test set must not publish its name to that editor.
  const activeTestSetIdRef = useRef(testSetId)
  useEffect(() => {
    activeTestSetIdRef.current = testSetId
    return () => {
      activeTestSetIdRef.current = undefined
    }
  }, [testSetId])

  // Rejects on failure so the title stays open with the message. The server's duplicate check backs up the client one for names this list does not hold.
  const renameTestSet = useCallback(
    async (name: string): Promise<void> => {
      if (!testSetId || !isPersisted) {
        return
      }

      try {
        await updateTestSetMutation.mutateAsync(
          mapUpdateTestSetInputToTransport({ testSetId, displayName: name }),
        )
      } catch (error) {
        if (isDuplicateTestSetNameError(error)) {
          throw new EditableTitleSaveError(duplicateTestSetNameMessage(name))
        }
        trackEvent(TRACKING_EVENTS.TEST_SET.NAME_UPDATE_FAILED, {
          error:
            error instanceof Error
              ? error.message
              : "Failed to update test set name",
          testSetId,
        })
        throw error
      } finally {
        void invalidateTestSetTransportQueries()
      }

      trackEvent(TRACKING_EVENTS.TEST_SET.RENAMED, { test_set_id: testSetId })
      if (activeTestSetIdRef.current === testSetId) {
        dispatch(EVENTS.METADATA_UPDATE, { displayName: name })
      }
    },
    [
      testSetId,
      isPersisted,
      updateTestSetMutation,
      dispatch,
      invalidateTestSetTransportQueries,
      trackEvent,
    ],
  )

  const handleOpenVersionHistory = useCallback(() => {
    dispatch(EVENTS.OPEN_VERSION_HISTORY, {})
  }, [dispatch])

  return {
    isTestSetDetailPage,
    testSetId,
    testSetMetadata,
    isCloning,
    isDeleting,
    isPersisted,
    isRenaming: updateTestSetMutation.isPending,
    handleOpenVersionHistory,
    currentDisplayName,
    records,
    recordsIsLoading,
    recordsIsError,
    recordsError,
    handleCloneTestSet,
    handleDownload,
    handleDeleteTestSet,
    handleConfirmDelete,
    handleCancelDelete,
    isDeleteModalOpen,
    metadataIsLoading,
    renameTestSet,
    validateTestSetName,
  }
}
